import { SystemKey, IntegrationType } from "../../../../../utils/enums";
export const getUniqueSystems = (users) => {
  // Show account-wise systems: use system ID + account_label as key
  const systemMap = new Map();
  
  users.forEach((userAccess) => {
    const system = userAccess.system;
    if (!system) return;
    
    // Exclude Zoho People only if it's not manual (allow manual Zoho People)
    const isZohoPeople = system.key?.toLowerCase() === SystemKey?.ZOHO_PEOPLE;
    if (isZohoPeople && (system.integration_type || '').toUpperCase() !== IntegrationType.MANUAL) return;
    
    const systemId = system.id;
    const accountLabel = system.account_label || null;
    // Create unique key: system ID + account_label (or just ID if no account_label)
    const key = accountLabel ? `${systemId}_${accountLabel}` : `${systemId}`;
    
    if (!systemMap.has(key)) {
      const displayLabel = accountLabel 
        ? `${system.label || "Unknown System"} (${accountLabel})`
        : system.label || "Unknown System";
      
      systemMap.set(key, {
        id: key, // Use key as ID for column field
        systemId: systemId, // Keep original system ID
        label: system.label || "Unknown System",
        accountLabel: accountLabel,
        displayLabel: displayLabel, // Label to display in header
        category: system.category || "N/A",
      });
    }
  });
  
  return Array.from(systemMap.values()).sort((a, b) => {
    // Sort by label first, then by account_label
    const labelCompare = a.label.localeCompare(b.label);
    if (labelCompare !== 0) return labelCompare;
    const accountA = a.accountLabel || "";
    const accountB = b.accountLabel || "";
    return accountA.localeCompare(accountB);
  });
};

export const transformUserAccessMatrix = (users, employees) => {
  const grouped = {};
  
  users.forEach((userAccess) => {
    if (!userAccess.email) return;
    
    const key = userAccess.email;
    const employee = employees.find((emp) => 
      emp.emails?.some((e) => e.email === userAccess.email)
    );
    
    if (!grouped[key]) {
      grouped[key] = {
        name: userAccess.name || "Unknown User",
        email: userAccess.email,
        orgRole: employee?.employments?.[0]?.job_title || "-",
        systemAccess: {},
      };
    }
    
    const system = userAccess.system;
    if (!system) return;
    
    // Exclude Zoho People only if it's not manual (allow manual Zoho People)
    const isZohoPeople = system.key?.toLowerCase() === SystemKey?.ZOHO_PEOPLE;
    if (isZohoPeople && (system.integration_type || '').toUpperCase() !== IntegrationType.MANUAL) return;
    
    const systemId = system.id;
    const accountLabel = system.account_label || null;
    // Create unique key: system ID + account_label (or just ID if no account_label)
    const systemKey = accountLabel ? `${systemId}_${accountLabel}` : `${systemId}`;
    const role = userAccess.role || "No Role";
    
    // If user already has access to this system account, keep the first role found
    // Or you could combine roles, but for now just use the first one
    if (!grouped[key].systemAccess[systemKey]) {
      grouped[key].systemAccess[systemKey] = {
        role: role === "-" ? "No Role" : role,
        reviewStatus: userAccess.review_status,
      };
    }
  });
  
  return Object.values(grouped).filter(
    (user) => Object.keys(user.systemAccess).length > 0
  );
};


export const getUniqueRoles = (userAccessMatrix) => {
  const roleSet = new Set(
    userAccessMatrix.map((u) => u.orgRole).filter(Boolean)
  );
  return Array.from(roleSet).filter((role) => role !== "-");
};

export const filterUsers = (userAccessMatrix, searchQuery, selectedRole) => {
  return userAccessMatrix.filter((user) => {
    const matchesSearch =
      !searchQuery ||
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.orgRole?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = !selectedRole || user.orgRole === selectedRole;

    return matchesSearch && matchesRole;
  });
};

